import type { ReactNode } from 'react'
import { Button, Group, Paper, SimpleGrid, Stack, Text, ThemeIcon } from '@mantine/core'
import { IconCircle, IconCircleCheck } from '@tabler/icons-react'
import { TooltipButton } from './TooltipButton'

type Props = {
  /** Where the patient takes the requisition, e.g. `Quest Diagnostics` / `radiology`. */
  placeName: string
  selectedFacilityId: string
  /** PD-1312 / PD-1642 — the patient carries a printable requisition to a location they pick. */
  patientChoosesLocation: boolean
  onPatientChoosesLocation: () => void
  /** Fax delivery chosen; the form drops the patient-chosen location. */
  onFaxDelivery: () => void
  /**
   * Fax delivery was chosen, even before any facility is picked. Kept by the form,
   * not this step: Back to the tests unmounts the step (Greptile !2060).
   */
  faxDeliveryChosen: boolean
  canAdvance: boolean
  onBack: () => void
  onNext: () => void
  /** The facility search, given the switch to patient delivery for its no-fax rows. */
  renderDiscovery: (choosePatient: () => void) => ReactNode
}

/**
 * PD-1929 / PD-1939 — the "Order delivery" step shared by the lab and radiology
 * orders. The provider settles how the order is delivered before anything else:
 * the patient brings a printable requisition, or it is faxed to a facility (which
 * then needs a fax number). The facility search only opens for fax delivery, so it
 * can no longer hide the patient option.
 */
export const OrderDeliveryStep = ({
  placeName,
  selectedFacilityId,
  patientChoosesLocation,
  onPatientChoosesLocation,
  onFaxDelivery,
  faxDeliveryChosen,
  canAdvance,
  onBack,
  onNext,
  renderDiscovery,
}: Props) => {
  const faxDelivery = !patientChoosesLocation && (faxDeliveryChosen || Boolean(selectedFacilityId))
  const choosePatient = onPatientChoosesLocation

  return (
    <Stack gap="lg">
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <DeliveryOption
          title="Patient will bring the order — no fax needed"
          description={`The patient chooses a ${placeName} location. The printable requisition becomes available in the patient portal after you submit the order.`}
          selected={patientChoosesLocation}
          onSelect={choosePatient}
        />
        <DeliveryOption
          title="Fax the order to a facility"
          description={`Select a ${placeName} facility and provide its fax number.`}
          selected={faxDelivery}
          onSelect={onFaxDelivery}
        />
      </SimpleGrid>
      {faxDelivery && renderDiscovery(choosePatient)}
      <Group justify="space-between">
        <Button variant="default" onClick={onBack}>
          Back
        </Button>
        <TooltipButton
          color="teal"
          onClick={onNext}
          disabled={!canAdvance}
          disabledReason={
            faxDelivery
              ? 'Select a facility and enter its fax number.'
              : 'Choose how the order is delivered.'
          }
        >
          Next: Order Details
        </TooltipButton>
      </Group>
    </Stack>
  )
}

type DeliveryOptionProps = {
  title: string
  description: string
  selected: boolean
  onSelect: () => void
}

// The two delivery choices, as a pair of equals.
const DeliveryOption = ({ title, description, selected, onSelect }: DeliveryOptionProps) => (
  <Paper
    p="md"
    withBorder
    role="button"
    tabIndex={0}
    aria-pressed={selected}
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        // Space would otherwise scroll the step instead of choosing the option.
        event.preventDefault()
        onSelect()
      }
    }}
    // Mantine's own Paper background/border rules outrank Tailwind `bg-*` /
    // `border-*` utilities, so the selected fill has to be set inline.
    style={selected ? { backgroundColor: '#EFF6FF', borderColor: '#3B82F6' } : undefined}
    className={
      selected
        ? 'cursor-pointer transition-all'
        : 'cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-sm'
    }
  >
    <Group gap="md" wrap="nowrap" align="flex-start">
      <ThemeIcon
        size={28}
        radius="xl"
        variant={selected ? 'filled' : 'light'}
        color={selected ? 'blue' : 'gray'}
      >
        {selected ? <IconCircleCheck size={20} /> : <IconCircle size={20} />}
      </ThemeIcon>
      <div>
        <Text fw={600} size="sm" c={selected ? 'blue' : undefined} mb={4}>
          {title}
        </Text>
        <Text size="sm" c="dimmed">
          {description}
        </Text>
      </div>
    </Group>
  </Paper>
)
